import { useEffect, useState } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Brain, ArrowLeft, Plus, Search, Clock, CheckCircle2, Play, Edit2, Users2, Zap, Target } from "lucide-react";
import { Link } from "react-router-dom";
import useAssessmentData from "../hooks/useAssessmentData";

/**
 * Quizzes — derived from the author's real QUIZ assessments.
 *
 * This page previously rendered six hard-coded quizzes ("JavaScript Fundamentals
 * Quiz", 20 questions, 18 attempts, 83% pass rate) that no API ever produced.
 * There is no standalone /quizzes endpoint, so a quiz here IS a QUIZ assessment
 * nested under a lesson. Figures the API does not report (attempt counts, pass rates,
 * average scores) are omitted rather than faked — the UI shows "—" instead of a
 * number that would be a lie.
 *
 * Source: GET /api/resource-person/courses           → the course picker
 *         GET /api/resource-person/courses/:id/lessons
 *         GET …/lessons/:lessonId/assessments → QUIZ assessments
 *         GET …/assessments/:assessmentId/questions → question count
 */
const statusConfig = {
  active: { label: "Live", cls: "bg-emerald-100 text-emerald-700", icon: Play },
  completed: { label: "Completed", cls: "bg-sky-100 text-sky-700", icon: CheckCircle2 },
  draft: { label: "Draft", cls: "bg-slate-100 text-slate-600", icon: Edit2 }
};

/** Render a metric honestly: a dash when the API gave us nothing. */
const metric = (value, suffix = "") =>
  value === null || value === undefined || Number.isNaN(Number(value))
    ? "—"
    : `${value}${suffix}`;

export default function QuizzesPage() {
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const {
    courses,
    coursesLoading,
    coursesError,
    selectedCourseId,
    setSelectedCourseId,
    lessons,
    lessonsCourseId,
    lessonsLoading,
    lessonsError,
    courseStudents,
    loadAssessments,
    loadQuestions,
  } = useAssessmentData();

  const [quizAssessments, setQuizAssessments] = useState([]);
  const [assessmentError, setAssessmentError] = useState(null);
  const [questionCounts, setQuestionCounts] = useState({});
  const lessonKey = (lessons || []).map((lesson) => lesson.id).join(",");

  useEffect(() => {
    let cancelled = false;
    setQuestionCounts({});
    setQuizAssessments([]);
    setAssessmentError(null);
    if (!selectedCourseId || String(lessonsCourseId) !== String(selectedCourseId) || !lessons.length) {
      return () => { cancelled = true; };
    }

    Promise.all(lessons.map(async (lesson) => {
      const result = await loadAssessments(selectedCourseId, lesson.id);
      if (!result.success) throw new Error(result.error || "Could not load lesson assessments.");
      return (result.assessments || [])
        .filter((assessment) => String(assessment.kind || "").toUpperCase() === "QUIZ")
        .map((assessment) => ({ ...assessment, lessonId: lesson.id, lessonTitle: lesson.title, courseId: selectedCourseId, lessonStatus: lesson.status }));
    }))
      .then((items) => {
        if (!cancelled) setQuizAssessments(items.flat());
      })
      .catch((error) => {
        if (!cancelled) setAssessmentError(error.message);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedCourseId, lessonsCourseId, lessonKey, loadAssessments]);

  useEffect(() => {
    let cancelled = false;
    if (!quizAssessments.length) return () => { cancelled = true; };
    Promise.all(quizAssessments.map(async (assessment) => {
      const result = await loadQuestions(assessment.courseId, assessment.lessonId, assessment.id);
      return result.success && Array.isArray(result.questions)
        ? [assessment.id, result.questions.length]
        : null;
    })).then((counts) => {
      if (!cancelled) setQuestionCounts((prev) => ({ ...prev, ...Object.fromEntries(counts.filter(Boolean)) }));
    });
    return () => { cancelled = true; };
  }, [quizAssessments, loadQuestions]);

  const quizzes = quizAssessments.map((assessment) => ({
      id: assessment.id,
      courseId: assessment.courseId,
      lessonId: assessment.lessonId,
      name: assessment.title || "Untitled quiz",
      questions: questionCounts[assessment.id] ?? null,
      timeLimit: assessment.timeLimit ?? null,
      // The API reports no per-quiz attempt/pass data to the author, so these
      // stay null and render as "—" rather than being invented.
      attempts: null,
      totalStudents: null,
      avgScore: null,
      passRate: null,
      topics: [],
      status: String(assessment.status || assessment.lessonStatus || "").toUpperCase(),
      lessonTitle: assessment.lessonTitle,
    }));

  const filtered = quizzes.filter((quiz) => {
    const matchFilter =
      filter === "all" ||
      (filter === "active" && quiz.status === "APPROVED") ||
      (filter === "completed" && quiz.status === "APPROVED") ||
      (filter === "draft" && (quiz.status === "DRAFT" || quiz.status === "PENDING"));
    const matchSearch = quiz.name.toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  });

  // Stats come only from what the API actually reported.
  const totalQuizzes = quizzes.length;
  const activeCount = quizzes.filter((q) => q.status === "APPROVED").length;
  const withPassData = quizzes.filter((q) => q.passRate !== null);
  const avgPassRate = withPassData.length
    ? Math.round(withPassData.reduce((s, q) => s + q.passRate, 0) / withPassData.length)
    : null;
  const totalAttempts = quizzes.reduce((s, q) => s + (q.attempts ?? 0), 0);
  const anyAttempts = quizzes.some((q) => q.attempts !== null);

  // Grading happens in the submissions inbox, so "View Results" goes there
  // rather than to a page with no data behind it.
  const resultsHref = selectedCourseId
    ? `/resource-person/content-library/course/${selectedCourseId}/submissions`
    : "/resource-person/assessment";

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-4 gradient-info rounded-2xl shadow-lg">
            <Brain className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-4xl font-extrabold tracking-tight mb-1">Quizzes</h1>
            <p className="text-muted-foreground text-lg">Build and manage interactive quiz assessments</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Link to="/resource-person/assessment">
            <Button variant="outline" size="lg" className="shadow-sm font-medium">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Button>
          </Link>
          {/* A quiz is created as a QUIZ lesson, which only the course builder can do —
            so this button takes the author there rather than pretending to open
            a form that has nowhere to save. */}
          {selectedCourseId ? (
            <Link to={`/resource-person/content-library/course/${selectedCourseId}/build`}>
              <Button size="lg" className="gradient-info text-white shadow-md font-semibold">
                <Plus className="mr-2 h-4 w-4" /> Create Quiz
              </Button>
            </Link>
          ) : (
            <Button size="lg" disabled className="font-semibold">
              <Plus className="mr-2 h-4 w-4" /> Create Quiz
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total Quizzes", value: totalQuizzes, icon: Brain, color: "text-info" },
          { label: "Active Now", value: activeCount, icon: Zap, color: "text-success" },
          { label: "Total Attempts", value: anyAttempts ? totalAttempts : "—", icon: Users2, color: "text-primary" },
          { label: "Avg Pass Rate", value: metric(avgPassRate, "%"), icon: Target, color: "text-warning" }
        ].map((stat, i) => (
          <Card key={i} className="border-0 shadow-md">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-muted-foreground">{stat.label}</p>
                <stat.icon className={`h-5 w-5 ${stat.color}`} />
              </div>
              <p className="text-3xl font-extrabold">{stat.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Course picker — the page is scoped to one course because the lesson
          and question endpoints are course-scoped. */}
      <div className="flex flex-col gap-2 rounded-xl border bg-muted/30 p-4 sm:flex-row sm:items-center">
        <label className="text-sm font-semibold" htmlFor="quiz-course">
          Course
        </label>
        <select
          id="quiz-course"
          className="flex-1 rounded-lg border bg-background px-3 py-2 text-sm"
          value={selectedCourseId}
          disabled={coursesLoading || !courses.length}
          onChange={(e) => setSelectedCourseId(e.target.value)}
        >
          {courses.length ? (
            courses.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title || "Untitled course"}
              </option>
            ))
          ) : (
            <option value="">{coursesLoading ? "Loading courses…" : "No courses yet"}</option>
          )}
        </select>
      </div>

      {coursesError ? (
        <p className="m-0 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">{coursesError}</p>
      ) : null}
      {lessonsError || assessmentError ? (
        <p className="m-0 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">{lessonsError || assessmentError}</p>
      ) : null}

      {/* Search + Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-info/30"
            placeholder="Search quizzes or courses..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          {["all", "active", "completed", "draft"].map(f => (
            <Button
              key={f}
              size="sm"
              variant={filter === f ? "default" : "outline"}
              onClick={() => setFilter(f)}
              className={filter === f ? "gradient-info text-white" : ""}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      {/* Quiz Cards */}
      <div className="grid gap-4 md:grid-cols-2">
        {!lessonsLoading && !filtered.length ? (
          <Card className="border-0 shadow-md md:col-span-2">
            <CardContent className="p-10 text-center">
              <Brain className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
              <p className="mb-1 font-semibold">
                {selectedCourseId ? "No quiz assessments in this course" : "Choose a course to see its quizzes"}
              </p>
              <p className="m-0 text-sm text-muted-foreground">
                {selectedCourseId
                  ? "Add a QUIZ assessment inside a lesson in the course builder."
                  : "Quizzes are shown per course."}
              </p>
            </CardContent>
          </Card>
        ) : null}
        {filtered.map(quiz => {
          // A lesson with no reported status is treated as a draft rather than
          // crashing on an undefined lookup.
          const statusKey =
            quiz.status === "APPROVED" ? "active" : quiz.status === "REJECTED" ? "completed" : "draft";
          const StatusIcon = statusConfig[statusKey].icon;
          const isDraft = statusKey === "draft";
          return (
            <Card key={quiz.id} className="border-0 shadow-md hover:shadow-xl transition-all duration-200 overflow-hidden">
              <CardContent className="p-6 space-y-4">
                {/* Title Row */}
                <div className="flex items-start justify-between gap-2">
                  <div className="space-y-1 flex-1">
                    <h3 className="font-bold text-lg leading-tight">{quiz.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {quiz.lessonTitle || "Lesson assessment"}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    <span className={`flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${statusConfig[statusKey].cls}`}>
                      <StatusIcon className="h-3 w-3" /> {statusConfig[statusKey].label}
                    </span>
                  </div>
                </div>

                {/* Meta — "—" wherever the API gave us nothing */}
                <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1.5"><Brain className="h-3.5 w-3.5" /> {metric(quiz.questions)} Questions</span>
                  <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {quiz.timeLimit != null ? `${quiz.timeLimit} mins` : "No time limit"}</span>
                  <span className="flex items-center gap-1.5"><Users2 className="h-3.5 w-3.5" /> {courseStudents.length} enrolled</span>
                </div>

                {/* Questions live in the builder — that is where they are edited. */}
                {!isDraft ? (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">
                      Attempt and pass-rate data is not reported to the author by the API, so it is
                      not shown here.
                    </p>
                  </div>
                ) : null}

                {/* Actions */}
                <div className="flex gap-2 pt-1">
                  {selectedCourseId ? (
                    <>
                      <Link to={`/resource-person/content-library/course/${quiz.courseId}/build`} className="flex-1">
                        <Button size="sm" className="w-full gradient-info text-white text-xs">
                          <Edit2 className="mr-1.5 h-3.5 w-3.5" /> Edit Questions
                        </Button>
                      </Link>
                      <Link to={resultsHref}>
                        <Button size="sm" variant="outline" className="text-xs">
                          View Submissions
                        </Button>
                      </Link>
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Choose a course above to edit this quiz.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}